/**
 * Rutas y assets de la tienda del vídeo, en un solo sitio y sin dependencias:
 * los importan la landing, el wizard, la entrega y el `cancel_url` del
 * checkout (infraestructura), y ninguno debe arrastrar al otro.
 */
import type { VideoStoryId } from '@/server/domain/santa-video/stories'

export const VIDEO_SHOP_PATH = '/video-papa-noel'
export const VIDEO_CREATE_PATH = `${VIDEO_SHOP_PATH}/crear`
export const VIDEO_BOOK_CREATE_PATH = `${VIDEO_SHOP_PATH}/crear-libro`

/**
 * El formulario de cada historia. Exhaustivo por tipo: es adonde vuelve quien
 * cancela el pago (`cancel_url`), y cada wizard recupera SU borrador.
 */
const VIDEO_CREATE_PATHS: { readonly [K in VideoStoryId]: string } = {
  letter: VIDEO_CREATE_PATH,
  book: VIDEO_BOOK_CREATE_PATH,
}

export function videoCreatePathOf(story: VideoStoryId): string {
  return VIDEO_CREATE_PATHS[story]
}

/**
 * El ancla de la ficha de cada historia en la landing, y el enlace a ella:
 * adonde llevan los cross-sells que nombran una historia concreta.
 */
export function videoCardIdOf(story: VideoStoryId): string {
  return `video-${story}`
}

export function videoCardHrefOf(story: VideoStoryId): string {
  return `${VIDEO_SHOP_PATH}#${videoCardIdOf(story)}`
}

/** El cross-sell a la videollamada: el registro, como los CTAs de la home. */
export const VIDEO_CALL_HREF = '/auth'

/**
 * El vídeo de ejemplo (pedido de prueba propio ac44d4d4, datos ficticios),
 * recomprimido a 720p con `+faststart` y servido por la CDN de Vercel. NO se
 * enlaza la URL firmada de Supabase: caduca a los siete días.
 *
 * El nombre lleva versión (`-v1`) porque el middleware sirve `.mp4`/`.webp`
 * como inmutables: regenerar el fichero con el mismo nombre dejaría la versión
 * vieja en las cachés. Al cambiarlo, sube la versión aquí y en el fichero.
 */
export const LETTER_SAMPLE_VIDEO = {
  src: '/videos/santa-video-ejemplo-v1.mp4',
  poster: '/videos/santa-video-ejemplo-v1-poster.webp',
} as const

/**
 * El ejemplo del Gran Libro (pedido de prueba propio aa3683fd, datos
 * ficticios, ya con los arreglos de voz), con las mismas reglas que `LETTER_SAMPLE_VIDEO`: 720p, `+faststart`,
 * CDN de Vercel y versión en el nombre. El póster es el fotograma clave de la
 * escena del libro que se abre.
 */
export const BOOK_SAMPLE_VIDEO = {
  src: '/videos/santa-video-libro-ejemplo-v2.mp4',
  poster: '/videos/santa-video-libro-ejemplo-v1-poster.webp',
} as const

export interface SampleVideoAsset { readonly src: string; readonly poster: string }

/** El ejemplo de cada historia: lo que reproduce su ficha en la landing. */
export const SAMPLE_VIDEOS: { readonly [K in VideoStoryId]: SampleVideoAsset } = {
  letter: LETTER_SAMPLE_VIDEO,
  book: BOOK_SAMPLE_VIDEO,
}

/**
 * Los fotogramas del collage del hero, en el orden en que se muestran: Papá
 * Noel (sale en las dos historias: `story: null`, siempre se muestra), luego el
 * libro (va antes en el año) y luego la carta. Los del libro son los
 * fotogramas clave con los que se generó su banco de clips; el de Papá Noel,
 * su imagen de referencia; los de la carta, capturas de `LETTER_SAMPLE_VIDEO`. Su pie
 * vive en la copy (`hero.frames`), con la misma clave. Llevan versión en el
 * nombre por lo mismo que los vídeos.
 */
export const HERO_FRAMES = [
  { id: 'santa', story: null, src: '/images/santa-video/hero/papa-noel-v1.webp' },
  { id: 'bookStairs', story: 'book', src: '/images/santa-video/hero/libro-escalera-v1.webp' },
  { id: 'bookOpens', story: 'book', src: '/images/santa-video/hero/libro-abre-v1.webp' },
  { id: 'bookNotes', story: 'book', src: '/images/santa-video/hero/libro-elfos-v1.webp' },
  { id: 'bookSeal', story: 'book', src: '/images/santa-video/hero/libro-sello-v1.webp' },
  { id: 'letterArrives', story: 'letter', src: '/images/santa-video/hero/carta-sobre-v1.webp' },
  { id: 'letterGift', story: 'letter', src: '/images/santa-video/hero/carta-regalo-v1.webp' },
  { id: 'letterSleigh', story: 'letter', src: '/images/santa-video/hero/carta-trineo-v1.webp' },
] as const satisfies readonly { id: string; story: VideoStoryId | null; src: string }[]

export type HeroFrameId = (typeof HERO_FRAMES)[number]['id']

/** La ruta de cada fotograma por su id, para quien lo pide por nombre (los cross-sells). */
export const HERO_FRAME_SRC = Object.fromEntries(HERO_FRAMES.map((frame) => [frame.id, frame.src])) as { readonly [K in HeroFrameId]: string }
